// The engine's numbers as the contract's names.
//
// **One table per enum, indexed by the enum, so a gap is a type error.** The
// contract says "pawn" where the rules say 1 and "N" where they say 0; these
// are the only places that translation is written down.

import type {
  ArmyName,
  BuildingName,
  Direction,
  ResourceName,
  PieceName,
  Side,
  Square,
  StructureName,
  VariantName
} from "src/api";
import {
  BISHOP,
  type Direction as Dir,
  KING,
  KNIGHT,
  PAWN,
  type PieceType,
  QUEEN,
  ROOK
} from "src/engine/rules/board";
import {
  ALTAR,
  SIMPLE_WALL,
  FIRM_WALL,
  REINFORCED_WALL,
  ARMORY,
  COAL_GENERATOR,
  NUCLEAR_GENERATOR,
  BATTERY,
  CABLING_TOWER,
  TRACK,
  TRAIN_CAR,
  TRAIN_ENGINE,
  KEYSTONE,
  RELIC,
  STORE,
  type StructureKind,
  TURRET,
  WELL,
  FUSION_CHAMBER,
  SHIELD_GENERATOR,
  ARTISAN_PILLAR,
  KINETIC_TURRET,
  CANNON_TURRET,
  PROPAGANDA_TOWER,
  HOUSE_OF_CARDS,
  ENDURANCE_MONUMENT,
  FIEND_FOUNTAIN,
  LEGION_TOWER,
  DRONE_WAREHOUSE,
  EMPOWER_TOWER
} from "src/engine/rules/industry/state";
import { FUEL, METAL, type MaterialLine, SPECIAL, STONE } from "src/engine/rules/materials";
import {
  CONFESSOR,
  DAM,
  DREADNOUGHT,
  ALICORN,
  GRYPHON,
  JOAN_OF_ARC,
  JOAN_QUEEN,
  MARSHAL,
  MATRIARCH,
  MIRACLE_HEALER,
  PALADIN,
  PEGASUS,
  PIECE_VARIANTS,
  TURBO_BISHOP,
  TURBO_ROOK,
  UNICORN,
  VARIANT_PLAIN
} from "src/engine/rules/rules";
import { KING_KIND, type KingKind } from "src/shared/board";

/** `KING_KIND` inverted: the contract's army names *are* the table's keys. */
export const ARMY_NAME: Record<KingKind, ArmyName> = Object.fromEntries(
  Object.entries(KING_KIND).map(([name, kind]) => [kind, name])
) as Record<KingKind, ArmyName>;

export const PIECE_NAME: Record<PieceType, PieceName> = {
  [PAWN]: "pawn",
  [KNIGHT]: "knight",
  [BISHOP]: "bishop",
  [ROOK]: "rook",
  [QUEEN]: "queen",
  [KING]: "king"
};

export const DIRECTION_NAME: Record<Dir, Direction> = ["N", "E", "S", "W"];

/**
 * The name a `StructureName` field carries. **A §31 S4–S6 kind says `store`**
 * — the human tree keys whole tables on `StructureName` — and its true name
 * rides beside it as `building` (`BUILDING_NAME`).
 */
export const STRUCTURE_NAME: Record<StructureKind, StructureName> = {
  [STORE]: "store",
  [TURRET]: "turret",
  [ALTAR]: "altar",
  [RELIC]: "relic",
  [KEYSTONE]: "keystone",
  [WELL]: "well",
  [SIMPLE_WALL]: "store",
  [FIRM_WALL]: "store",
  [REINFORCED_WALL]: "store",
  [ARMORY]: "store",
  [COAL_GENERATOR]: "store",
  [NUCLEAR_GENERATOR]: "store",
  [BATTERY]: "store",
  [CABLING_TOWER]: "store",
  [TRACK]: "store",
  [TRAIN_ENGINE]: "store",
  [TRAIN_CAR]: "store",
  [FUSION_CHAMBER]: "store",
  [SHIELD_GENERATOR]: "store",
  [ARTISAN_PILLAR]: "store",
  // §31 S8: every gun says `turret` to the frozen union; `building` tells them apart.
  [KINETIC_TURRET]: "turret",
  [CANNON_TURRET]: "turret",
  [PROPAGANDA_TOWER]: "store",
  [HOUSE_OF_CARDS]: "store",
  [ENDURANCE_MONUMENT]: "store",
  [FIEND_FOUNTAIN]: "store",
  [LEGION_TOWER]: "store",
  [DRONE_WAREHOUSE]: "store",
  [EMPOWER_TOWER]: "store"
};

/** Every kind's true name (§31 S4–S6). Option ids and the `building` fields use it. */
export const BUILDING_NAME: Record<StructureKind, BuildingName> = {
  [STORE]: "store",
  [TURRET]: "turret",
  [ALTAR]: "altar",
  [RELIC]: "relic",
  [KEYSTONE]: "keystone",
  [WELL]: "well",
  [SIMPLE_WALL]: "simple-wall",
  [FIRM_WALL]: "firm-wall",
  [REINFORCED_WALL]: "reinforced-wall",
  [ARMORY]: "armory",
  [COAL_GENERATOR]: "coal-generator",
  [NUCLEAR_GENERATOR]: "nuclear-generator",
  [BATTERY]: "battery",
  [CABLING_TOWER]: "cabling-tower",
  [TRACK]: "track",
  [TRAIN_ENGINE]: "train-engine",
  [TRAIN_CAR]: "train-car",
  [FUSION_CHAMBER]: "fusion-chamber",
  [SHIELD_GENERATOR]: "shield-generator",
  [ARTISAN_PILLAR]: "artisan-pillar",
  [KINETIC_TURRET]: "kinetic-turret",
  [CANNON_TURRET]: "cannon-turret",
  [PROPAGANDA_TOWER]: "propaganda-tower",
  [HOUSE_OF_CARDS]: "house-of-cards",
  [ENDURANCE_MONUMENT]: "endurance-monument",
  [FIEND_FOUNTAIN]: "fiend-fountain",
  [LEGION_TOWER]: "legion-tower",
  [DRONE_WAREHOUSE]: "drone-warehouse",
  [EMPOWER_TOWER]: "empower-tower"
};

export const LINE_NAME: Record<MaterialLine, ResourceName> = {
  [METAL]: "metal",
  [STONE]: "stone",
  [SPECIAL]: "special",
  [FUEL]: "fuel"
};

const VARIANT_BY_ID: Record<number, VariantName> = {
  [VARIANT_PLAIN]: "plain",
  [MARSHAL]: "marshal",
  [DAM]: "dam",
  [TURBO_BISHOP]: "turbo-bishop",
  [TURBO_ROOK]: "turbo-rook",
  [JOAN_QUEEN]: "joan",
  [MATRIARCH]: "matriarch",
  [CONFESSOR]: "confessor",
  [MIRACLE_HEALER]: "healer",
  [PEGASUS]: "pegasus",
  [UNICORN]: "unicorn",
  [GRYPHON]: "gryphon",
  [PALADIN]: "paladin",
  [DREADNOUGHT]: "dreadnought",
  [ALICORN]: "alicorn",
  [JOAN_OF_ARC]: "joan-of-arc"
};

/** Every row of `PIECE_VARIANTS` has a name here; `names.test.ts` walks the table. */
export const VARIANT_NAME: readonly VariantName[] = PIECE_VARIANTS.map((_row, id) => {
  const name = VARIANT_BY_ID[id];
  if (name === undefined) throw new Error(`PIECE_VARIANTS[${id}] has no contract name`);
  return name;
});

/** The brands, applied. Numbers in, the contract's types out — nothing else changes. */
export const asSquare = (sq: number): Square => sq as Square;
export const asSide = (side: number): Side => side as Side;
